import type { Channel } from './types';

/** Spec 4.6: load messages are exactly 1,200 bytes; the server refuses any other size. */
export const LOAD_MESSAGE = 1200;
const PAYLOAD = new ArrayBuffer(LOAD_MESSAGE);
/** The quota never holds more than this much time worth of data, so a stuck channel is not caught up. */
const MAX_QUOTA_MS = 50;

/**
 * Sends the upload load at a target rate. A message goes out only while the channel's buffer is
 * below one message (spec 4.6): what SCTP does not accept is not queued, and the shortfall shows
 * as a sent rate below the target.
 */
export class LoadSender {
    private kbps = 0;
    private quota = 0;
    private last?: number;

    constructor(
        private readonly channel: Channel,
        private readonly onSent: (bytes: number) => void
    ) {}

    setRate(kbps: number): void {
        this.kbps = Math.max(0, kbps);
        if (this.kbps === 0) this.quota = 0;
    }

    tick(nowMono: number): void {
        const bytesPerMs = (this.kbps * 1000) / 8 / 1000;
        if (this.last !== undefined) {
            const cap = Math.max(LOAD_MESSAGE, bytesPerMs * MAX_QUOTA_MS);
            this.quota = Math.min(cap, this.quota + bytesPerMs * (nowMono - this.last));
        }
        this.last = nowMono;
        this.pump();
    }

    /** Sends what the quota allows while the channel is not holding a message; also called on drain. */
    pump(): void {
        while (this.kbps > 0 && this.quota >= LOAD_MESSAGE && this.channel.bufferedAmount() < LOAD_MESSAGE) {
            this.channel.send(PAYLOAD);
            this.quota -= LOAD_MESSAGE;
            this.onSent(LOAD_MESSAGE);
        }
    }
}
